import { GAME_ID, GAME_MODES, REDUX_ACTIONS, CLAIMS_PER_ROUND, ITEMS_PER_CLAIM } from '../../data/constants';
import { isLastItemInClaim, isLastClaimInLevel } from '../selectors/roundPlaySelectors';
import { getUserEvidenceOptions, getOnboardingOptions } from '../selectors/curriculumSelectors';
import { getLevelCount } from '../../content/curriculum';

const setEvidencePositions = (evidenceArray, order) => {
  const newArray = [];
  newArray.push(evidenceArray[order[0]]);
  newArray.push(evidenceArray[order[1]]);
  newArray.push(evidenceArray[order[2]]);
  return newArray;
};

const initialOptionState = {
  isSelected: false,
  isFaded: false,
  isEnabled: true,
};

const initState = {
  gameMode: GAME_MODES.INACTIVE,
  // Curriculum Pointers
  levelIndex: 0,
  // Each level has 2 sides of content
  levelSideIndices: [0, 0, 0, 0, 0, 0, 0, 0],
  // each side has 3 claims
  claimIndex: 0,
  // each claim has 3 items
  itemIndex: 0,
  userEvidenceOptions: [],
  optionStates: [
    initialOptionState,
    initialOptionState,
    initialOptionState,
  ],
  progressAmount: 0, // The percentage amount of the round that user has completed
  responseAttemptCount: 0,
  selectedResponse: undefined,
  questionsAnsweredThisRound: 0,
  questionsAnsweredCorrectlyThisRound: 0,
  computerAnswersCorrectThisRound: 0,
  wasIncorrectComputerAnswerInRound: false,
  isIncorrectItemsFaded: false, // show whether currently incorrect answer items are faded
  isOnboardingFromHelpButton: false,
  isComputerThinking: false, // show whether currently computer is thinking
  isOnlyRightAnswerHighlighted: false, // show whether right answer is highlighted after incorrect answer
};

const roundPlayReducer = (state = initState, { type, payload, gameId }) => {
  if (gameId !== GAME_ID) {
    return state;
  }

  switch (type) {
    case REDUX_ACTIONS.SET_LEVEL_COUNT:
      return {
        ...state,
        levelCount: payload,
        levelSideIndices: new Array(payload).fill(0),
      };
    case REDUX_ACTIONS.INITIALIZE_ROUND:
      return {
        ...state,
        gameMode: GAME_MODES.STANDARD_ITEM,
        levelIndex: payload,
        questionsAnsweredCorrectlyThisRound: 0,
        questionsAnsweredThisRound: 0,
        progressAmount: 0,
        itemIndex: 0,
        claimIndex: 0,
        wasIncorrectComputerAnswerInRound: false,
        isIncorrectItemsFaded: false,
        levelSideIndices: Array(getLevelCount()).fill(false),
      };

    case REDUX_ACTIONS.NEXT_STANDARD_ITEM: {
      // If there are no more question items, end the round
      if (isLastItemInClaim(state)) {
        if (isLastClaimInLevel(state) &&
            (state.levelIndex !== -1 || state.isOnboardingFromHelpButton)) {
          // If last item in a standard round OR an "onboarding only" round, then we are 100% done with round
          return {
            ...initState,
            progressAmount: 100,
            isIncorrectItemsFaded: false,
          };
        }
        // -1 indicates you're in onboarding - below is after last selection in onboarding before a standard round
        if (state.levelIndex === -1) {
          return {
            ...initState,
            gameMode: GAME_MODES.CLAIM_INTRODUCTION,
            userEvidenceOptions: setEvidencePositions(
              getUserEvidenceOptions(
                0,
                0,
                0,
                0,
              ),
              payload.evidenceOrder,
            ),
          };
        }
        return {
          ...state,
          optionStates: initState.optionStates.map(option => ({ ...option })),
          itemIndex: 0,
          levelIndex: state.levelIndex,
          claimIndex: state.claimIndex + 1,
          gameMode: GAME_MODES.CLAIM_INTRODUCTION,
          progressAmount: state.progressAmount + (100 / (CLAIMS_PER_ROUND * ITEMS_PER_CLAIM)),
          isIncorrectItemsFaded: false,
          userEvidenceOptions: setEvidencePositions(
            getUserEvidenceOptions(
              state.levelIndex,
              state.levelSideIndices[state.levelIndex],
              state.claimIndex + 1,
              0,
            ),
            payload.evidenceOrder,
          ),
        };
      }
      return {
        ...state,
        optionStates: initState.optionStates.map(option => ({ ...option })),
        gameMode: state.levelIndex === -1 ? state.gameMode : GAME_MODES.STANDARD_ITEM,
        itemIndex: state.itemIndex + 1,
        progressAmount: state.levelIndex === -1 ?
          state.progressAmount + (100 / ITEMS_PER_CLAIM) :
          state.progressAmount + (100 / (CLAIMS_PER_ROUND * ITEMS_PER_CLAIM)),
        responseAttemptCount: 0,
        isIncorrectItemsFaded: false,
        userEvidenceOptions: state.levelIndex === -1 ?
          getOnboardingOptions(state.itemIndex + 1) :
          setEvidencePositions(
            getUserEvidenceOptions(
              state.levelIndex,
              state.levelSideIndices[state.levelIndex],
              state.claimIndex,
              state.itemIndex + 1,
            ),
            payload.evidenceOrder,
          ),
      };
    }

    case REDUX_ACTIONS.ROUND_START:
      return {
        ...initState,
        gameMode: payload.levelIndex === -1 ? GAME_MODES.ONBOARDING : GAME_MODES.STANDARD_ITEM,
        levelIndex: payload.levelIndex,
        isOnboardingFromHelpButton: payload.isOnboardingFromHelpButton,
        levelSideIndices: state.levelSideIndices,
        userEvidenceOptions: payload.levelIndex === -1 ?
          getOnboardingOptions(0) :
          setEvidencePositions(
            getUserEvidenceOptions(
              payload.levelIndex,
              state.levelSideIndices[payload.levelIndex],
              0,
              0,
            ),
            payload.evidenceOrder,
          ),
      };
    case REDUX_ACTIONS.ROUND_END: {
      // Reset to initial state when round is complete
      const newLevelSideIndices = [...state.levelSideIndices];
      newLevelSideIndices[state.levelIndex] = state.levelSideIndices[state.levelIndex] === 0 ? 1 : 0;
      return {
        ...state,
        gameMode: GAME_MODES.INACTIVE,
        // flip the side of the level so the next time you play it, you get different content
        levelSideIndices: newLevelSideIndices,
        selectedResponse: undefined,
        wasIncorrectComputerAnswerInRound: false,
      };
    }
    case REDUX_ACTIONS.SHOW_CORRECT_RESPONSE:
      return {
        ...state,
        gameMode: GAME_MODES.FEEDBACK_SHOW_CORRECT,
      };

    case REDUX_ACTIONS.SELECT_RESPONSE: {
      const { response, isCorrect } = payload;
      const questionsAnsweredCorrectlyThisRound = isCorrect ?
        state.questionsAnsweredCorrectlyThisRound + 1 :
        state.questionsAnsweredCorrectlyThisRound;
      return {
        ...state,
        selectedResponse: response,
        questionsAnsweredThisRound: state.questionsAnsweredThisRound + 1,
        questionsAnsweredCorrectlyThisRound,
      };
    }

    case REDUX_ACTIONS.INCREMENT_COMPUTER_POSITIVE_RESPONSES: {
      return {
        ...state,
        computerAnswersCorrectThisRound: state.computerAnswersCorrectThisRound + 1,
      };
    }

    case REDUX_ACTIONS.SET_GAME_MODE:
      return {
        ...state,
        gameMode: payload,
      };

    case REDUX_ACTIONS.NEXT_ITEM:
      return {
        ...state,
        itemIndex: state.itemIndex + 1,
        isIncorrectItemsFaded: false,
      };

    case REDUX_ACTIONS.SET_FIRST_INCORRECT_COMPUTER_ANSWER:
      return {
        ...state,
        wasIncorrectComputerAnswerInRound: true,
      };

    case REDUX_ACTIONS.SET_INCORRECT_ITEMS_FADED:
      return {
        ...state,
        isIncorrectItemsFaded: true,
      };

    case REDUX_ACTIONS.COMPLETE_ONBOARDING:
      return {
        ...initState,
        userEvidenceOptions: setEvidencePositions(
          getUserEvidenceOptions(0, 0, 0, 0),
          payload.evidenceOrder,
        ),
      };

    case REDUX_ACTIONS.SET_OPTION_STATE: {
      const newOptionStates = state.optionStates.map(option => ({ ...option }));
      newOptionStates[payload.optionIndex][payload.field] = payload.value;
      return {
        ...state,
        optionStates: newOptionStates,
      };
    }
    case REDUX_ACTIONS.SET_COMPUTER_THINKING:
      return {
        ...state,
        isComputerThinking: payload,
      };

    case REDUX_ACTIONS.SET_ONLY_RIGHT_ANSWER_HIGHLIGHTED:
      return {
        ...state,
        isOnlyRightAnswerHighlighted: payload,
      };

    case REDUX_ACTIONS.QUICK_ROUND: {
      const questionCount = ITEMS_PER_CLAIM * CLAIMS_PER_ROUND;
      const questionsCorrect = payload ? questionCount : 0;
      return {
        ...state,
        progressAmount: 100,
        questionsAnsweredCorrectlyThisRound: questionsCorrect,
        questionsAnsweredThisRound: questionCount,
      };
    }
    default:
      return state;
  }
};

export default roundPlayReducer;
